<template>
    <el-row class="pie-chart-with-list">
        <el-col :sm="8">
            <v-chart
                ref="pieChart"
                :option="pieChartOption"
                style="width: 100%; height: 250px"
                @mouseover="handlePieChartMouseOver"
                @mouseout="handlePieChartMouseOut"
                @focus="() => {}"
                @blur="() => {}"
            />
        </el-col>
        <el-col :sm="16">
            <div class="list-area">
                <div class="total">
                    <div class="total-title">
                        <span>{{ this.year }}年營業總{{ category }}</span>
                        <ahTip v-if="totalToolTip != null">{{ totalToolTip }}</ahTip>
                        <slot name="header" />
                    </div>
                    <div class="total-value">
                        <Money :v="totalValue" round />
                    </div>
                </div>
                <ul class="list">
                    <li class="list-item-header">
                        <span class="">{{ category }}項目</span>
                        <span class="right">總金額</span>
                        <span class="right" :class="{ toggle }" @click="handleToggle" @keypress="() => {}"> 占總{{ toggleActive ? toggle.label : category }}% </span>
                    </li>
                    <li
                        v-for="(item, index) in itemsFromChart"
                        :key="item.index"
                        :class="{ active: activeIndex === index }"
                        class="list-item"
                        @mouseover="handleListItemMouseOver(index)"
                        @mouseleave="handleListItemMouseLeave(index)"
                        @focus="() => {}"
                        @blur="() => {}"
                    >
                        <span class="cell name">
                            <span class="color-dot" :style="{ 'background-color': item.color }"> </span>
                            <span>{{ item.name }}</span>
                        </span>
                        <span class="cell right value">
                            <Money :v="item.value" round />
                        </span>
                        <span class="cell right percent"> {{ item.percent ? item.percent : "--" }} % </span>
                    </li>
                </ul>
            </div>
        </el-col>
    </el-row>
</template>
<script>
import _ from 'lodash';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { PieChart } from 'echarts/charts';
import {
    TitleComponent, TooltipComponent, LegendComponent, GridComponent, ToolboxComponent,
} from 'echarts/components';

import VChart from 'vue-echarts';

use([CanvasRenderer, PieChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent, ToolboxComponent]);

export default {
    name: 'PieChartWithList',
    components: {
        VChart,
    },
    props: {
        data: {
            type: Array,
            default() {
                return [];
            },
        },
        year: {
            type: Number,
            dafault: 0,
        },
        category: {
            type: String,
            dafault: 'Total',
        },
        totalToolTip: {
            type: String,
            dafault: null,
        },
        toggle: {
            type: Object,
            default: () => {},
        },
        totalValue: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            activeIndex: null,
            itemsFromChart: [],
            pieChartOption: {
                tooltip: {
                    trigger: 'item',
                    formatter: (item) => `${item.name} ${Math.round((item.value / this.totalValue) * 1000) / 10} %`,
                    confine: true,
                },
                series: [
                    {
                        type: 'pie',
                        radius: ['38%', '63%'],
                        center: ['50%', '50%'],
                        avoidLabelOverlap: false,
                        label: {
                            show: false,
                        },
                        animation: false,
                        data: [],
                        emphasis: {
                            scaleSize: 10,
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 2,
                                shadowColor: 'rgba(0, 0, 0, 0.2)',
                            },
                        },
                    },
                ],
            },
            toggleActive: false,
        };
    },
    watch: {
        data(newVal) {
            if (this.toggleActive) this.handleToggle();
            const newOption = { ...this.pieChartOption };
            newOption.series[0].data = newVal;
            this.pieChartOption = { ...newOption };
            setTimeout(() => {
                this.getItemsFromChart();
            }, 1);
        },
    },
    mounted() {
        window.addEventListener('resize', this.updateChartSize);
    },
    destroyed() {
        window.removeEventListener('resize', this.updateChartSize);
    },
    methods: {
        handlePieChartMouseOver(params) {
            this.activeIndex = params.dataIndex;
        },
        handlePieChartMouseOut() {
            this.activeIndex = null;
        },
        handleListItemMouseOver(index) {
            this.$refs.pieChart.dispatchAction({
                type: 'highlight',
                dataIndex: index,
            });
        },
        handleListItemMouseLeave(index) {
            this.$refs.pieChart.dispatchAction({
                type: 'downplay',
                dataIndex: index,
            });
        },
        getItemsFromChart() {
            const option = this.$refs.pieChart.getOption();

            this.itemsFromChart = [];
            option.series[0].data.forEach((item, index) => {
                const newItem = {
                    index,
                    name: item.name,
                    value: item.value,
                    color: option.color[index % option.color.length],
                    percent: Math.round((item.value / this.totalValue) * 1000) / 10,
                };

                this.itemsFromChart.push(newItem);
            });
        },
        handleToggle() {
            this.toggleActive = !this.toggleActive;

            if (this.toggleActive) {
                this.itemsFromChart = _.map(this.itemsFromChart, (o) => ({
                    ...o,
                    percent: Math.round((o.value / this.toggle.totalValue) * 1000) / 10,
                }));
            } else {
                this.itemsFromChart = _.map(this.itemsFromChart, (o) => ({
                    ...o,
                    percent: Math.round((o.value / this.totalValue) * 1000) / 10,
                }));
            }
        },
        updateChartSize() {
            this.$refs.pieChart.resize();
        },
    },
};
</script>
<style lang="scss">
@import "@/assets/scss/vars.scss";

.pie-chart-with-list {
    .list-area {
        padding: 0 25px 0 10px;

        @media (max-width: 1024px) {
            padding: 0 10px 0 5px;
        }

        .total {
            padding: 0 0 0 10px;

            .total-title {
                color: $main-color;
                font-size: 11pt;
                margin-bottom: 0px;
                margin-top: 5px;
            }

            .total-value {
                padding-bottom: 25px;
                margin-bottom: 15px;
                border-bottom: 1px solid #eee;

                .money {
                    font-size: 17pt !important;
                    font-weight: normal;
                }
            }
        }

        .list {
            list-style: none;
            padding: 0;
            display: table;
            width: 100%;
            cursor: default;

            .list-item {
                font-size: 11pt;

                &.active,
                &:hover {
                    transform: scale(1.07);

                    .cell,
                    .cell:first-child,
                    .cell:last-child {
                        background-color: #f4f4f4;
                    }
                }

                .color-dot {
                    display: inline-block;
                    position: relative;
                    top: -1px;
                    width: 10px;
                    height: 10px;
                    margin: 0 12px 0 0;
                    border-radius: 5px;
                    vertical-align: middle;
                }
            }

            .list-item-header {
                color: #aaa;
                font-size: 10pt;
                .toggle {
                    cursor: pointer;
                    text-decoration: underline;
                }
            }

            [class^="list-item"] {
                display: table-row;
                transition: 0.2s;
                > * {
                    display: table-cell;
                    padding: 0 3px;
                    box-sizing: border-box;
                    border-top: 2px solid transparent;
                    border-bottom: 2px solid transparent;

                    &:first-child {
                        padding: 0 0 0 10px;
                        border-top-left-radius: 20px;
                        border-bottom-left-radius: 20px;
                        border-left: 2px solid transparent;
                    }

                    &:last-child {
                        padding: 0 10px 0 0;
                        border-top-right-radius: 20px;
                        border-bottom-right-radius: 20px;
                        border-right: 2px solid transparent;
                    }
                }
                .right {
                    text-align: right;
                }
            }
        }
    }
}
</style>
